import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { CommonDashboardWrapper } from "styles/GlobalStyles";

import {
  managerDropdownSelector,
  managerListSelector,
  removeMemberStatusSelector,
  talentAgencyCreatorsSelector
} from "pages/TalentAgencies/slice/selector";
import { selectUser } from "store/auth/slice/selector";
import { useTalentAgenciesSlice } from "pages/TalentAgencies/slice/index";

import Icon from "components/Icon/Icon";
import Input from "components/Input/index";
import Button from "components/Buttons/index";
import CommonTable from "components/Tables/CommonTable";
import PillButtons from "components/Buttons/PillButtons";
import LoadingSpinner from "components/PageLoader/Spinner";
import SkeletonLoader from "components/PageLoader/SkeletonLoader";
import RosterUpdateModal from "components/Modal/RosterUpdateModal/index";
import TalentAgencyManagerModal from "components/Modal/TalentAgencyManagerModal/index";
import RemoveTeamMember from "components/Modal/TalentAgencyManagerModal/RemoveTeamMember";

import { SearchWrapper, TabButton, TabsWrapper } from "./styled";

import CreatorColumns from "pages/TalentAgencies/AgencyPage/columns/creatorColumn";
import ManagerColumns from "pages/TalentAgencies/AgencyPage/columns/managerColumn";

import { CompanyNameCell, renderValue } from "utils/renderCompanyCell";

import {
  ArrowLeftIcon,
  CalendarBlankIcon,
  UserCircleCheckIcon,
  UserCircleGearIcon,
  CaretDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
  GearIcon
} from "@phosphor-icons/react";

import { CompaniesWrapperStyles } from "pages/Companies/styled";
import OptedInLeads from "../OptedInLeads/index";

const AgencyPage = () => {
  const { actions } = useTalentAgenciesSlice();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { agencyId } = useParams();

  // Get query params
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type") || "creators";
  const initialPage = Number(queryParams.get("page")) || 1;
  const initialLimit = Number(queryParams.get("limit")) || 10;
  const initialSearch = queryParams.get("search") || "";

  // --- Local states ---
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalMode, setModalMode] = useState("add");
  const [removeModal, setRemoveModal] = useState({
    show: false,
    row: null,
    step: "confirm", // confirm | reassign | cannot_reassign
    creators: []
  });
  const [showRosterModal, setShowRosterModal] = useState(false);

  const {
    creators,
    teamMembers,
    agency_details,
    loading,
    pagination,
    initialLoading
  } = useSelector(talentAgencyCreatorsSelector);
  const { user } = useSelector(selectUser);

  const { managers: managerDropdown } = useSelector(managerDropdownSelector);
  const removeMember = useSelector(removeMemberStatusSelector);
  const { managers, loading: loadingList } = useSelector(managerListSelector);

  useEffect(() => {
    if (agencyId) {
      dispatch(actions.fetchManagerDropdown({ agencyId }));
    }
  }, [agencyId]);

  // --- Debounce search input ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(
      actions.fetchTalentAgencyMembers({
        agencyId,
        type,
        page: currentPage,
        limit: initialLimit,
        search: debouncedSearch,
        manager_id: selectedManager?.value || null
      })
    );
  }, [
    agencyId,
    type,
    currentPage,
    initialLimit,
    debouncedSearch,
    selectedManager?.value
  ]);

  useEffect(() => {
    if (!removeMember?.status) return;

    // REMOVED SUCCESSFULLY
    if (removeMember.status === true) {
      setRemoveModal({
        show: false,
        row: null,
        step: "confirm",
        creators: []
      });
    }

    // ONLY ONE MANAGER → show info INSIDE modal
    if (removeMember.status === "reassign_failure") {
      setRemoveModal((prev) => ({
        ...prev,
        show: true,
        step: "cannot_reassign"
      }));
    }

    // REASSIGN REQUIRED → show dropdown UI
    if (removeMember.status === "reassign_success") {
      setRemoveModal((prev) => ({
        ...prev,
        show: true,
        step: "reassign",
        creators: removeMember.data.creators
      }));
    }
  }, [removeMember.status]);

  useEffect(() => {
    if (
      removeModal.show &&
      removeModal.step === "reassign" &&
      removeModal.row?.talent_manager_id
    ) {
      dispatch(
        actions.fetchManagersList({
          talent_company_id: agencyId,
          exclude_manager_id: removeModal.row.talent_manager_id
        })
      );
    }
  }, [removeModal.step, removeModal.show, removeModal.row?.id, agencyId]);

  const handleGoBack = () => {
    const prevSearch = location.state?.prevSearch || "";
    const lastTab = location.state?.lastTab || {};

    navigate(`/talent-agencies${prevSearch}`, { state: { lastTab } });
  };

  const handleTabChange = (newType) => {
    const lastTab = {
      ...(location.state?.lastTab || {}),
      [agencyId]: newType
    };
    navigate(`/talent-agencies/${agencyId}?type=${newType}`, {
      replace: false,
      state: { prevSearch: location.state?.prevSearch, lastTab }
    });
  };

  const CreatorTableColumns = useMemo(() => CreatorColumns(), []);
  const ManagerTableColumns = useMemo(
    () =>
      ManagerColumns({
        onEdit: (row) => {
          setSelectedRow(row);
          setModalMode("edit");
          setShowEditModal(true);
        },
        onRemove: (row) =>
          setRemoveModal({
            show: true,
            row,
            step: "confirm",
            creators: []
          })
      }),
    []
  );

  // --- Page change handler ---
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleStatusSelect = (item) => {
    setSelectedManager(item);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedRow(null);
    setModalMode("add");
  };

  const handleRemoveTeamMember = (row) => {
    dispatch(
      actions.removeTeamMember({
        userId: row.id,
        talent_company_id: agencyId
      })
    );
  };

  const handleReassignAndRemove = ({ removedManagerId, newManagerId }) => {
    dispatch(
      actions.reassignCreatorsToManager({
        from_manager_id: removedManagerId,
        to_manager_id: newManagerId
        // talent_company_id: agencyId
      })
    );
  };

  return (
    <>
      <CommonDashboardWrapper>
        <CompaniesWrapperStyles>
          {initialLoading ? (
            <SkeletonLoader width="600px" height="34px" />
          ) : (
            <div className="header-wrapper">
              <div className="go-back-wrapper">
                <div className="go-back-icon" onClick={handleGoBack}>
                  <Icon
                    component={ArrowLeftIcon}
                    format="outline"
                    weight="regular"
                  />
                </div>
                <div className="update-text-wrapper">
                  <CompanyNameCell
                    logo={agency_details?.logo}
                    name={renderValue(agency_details?.company_name)}
                    // suffix="&lsquo;s"
                  />
                </div>
              </div>

              <Button
                variant="secondary"
                onClick={() => setShowRosterModal(true)}
              >
                <Icon component={CalendarBlankIcon} size={16} /> Roster Updated
                at
              </Button>
            </div>
          )}
        </CompaniesWrapperStyles>

        <TabsWrapper>
          {initialLoading ? (
            <SkeletonLoader height="41px" width="350px" />
          ) : (
            <>
              <TabButton
                className={type === "team_members" ? "active" : ""}
                onClick={() => {
                  handleTabChange("team_members");
                }}
              >
                <Icon
                  component={UserCircleGearIcon}
                  color={type === "team_members" ? "#1F1F1F" : "#4C4C4C"}
                  size={16}
                  format="outline"
                  weight="regular"
                />
                <span>
                  Team Members ({agency_details?.total_team_members ?? 0})
                </span>
              </TabButton>

              <TabButton
                className={type === "creators" ? "active" : ""}
                onClick={() => {
                  handleTabChange("creators");
                }}
              >
                <Icon
                  component={UserCircleCheckIcon}
                  color={type === "creators" ? "#1F1F1F" : "#4C4C4C"}
                />
                <span>Creators ({agency_details?.total_creators ?? 0})</span>
              </TabButton>

              <TabButton
                className={type === "settings" ? "active" : ""}
                onClick={() => {
                  handleTabChange("settings");
                }}
              >
                <Icon
                  component={GearIcon}
                  color={type === "settings" ? "#1F1F1F" : "#4C4C4C"}
                />
                <span>Settings</span>
              </TabButton>
            </>
          )}
        </TabsWrapper>

        {type === "creators" && (
          <PillButtons
            placeholderText="Manager"
            statusPlaceholder="statusActive"
            mainLabelText={selectedManager?.label}
            leftIcon={
              <Icon
                component={UserCircleGearIcon}
                format="outline"
                weight="regular"
                size={12}
              />
            }
            rightIcon={
              <Icon
                component={CaretDownIcon}
                format="outline"
                weight="regular"
                size={12}
              />
            }
            activeRightIcon={
              <Icon
                component={XIcon}
                format="outline"
                weight="regular"
                size={12}
              />
            }
            menuItems={managerDropdown?.map((manager) => ({
              label: manager.name,
              value: manager.id,
              count: manager.creator_count
            }))}
            onSelect={handleStatusSelect}
            size="M"
            maxWidth="144px"
          />
        )}

        {type === "team_members" && (
          <SearchWrapper>
            <Input
              id="searchManagerName"
              type="search"
              placeholder="Search by team member name"
              leftIcon={
                <Icon
                  component={MagnifyingGlassIcon}
                  format="outline"
                  weight="regular"
                />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="S"
              className="search-input"
              rightIcon={
                loading ? (
                  <LoadingSpinner color="#1f1f1f" size={12} />
                ) : (
                  <Icon component={XIcon} format="outline" weight="regular" />
                )
              }
              handleClearSearch={handleClearSearch}
            />

            <Button
              size="S"
              variant="primary"
              leftIcon={
                <Icon
                  component={PlusIcon}
                  format="outline"
                  weight="regular"
                  color="#FFFFFF"
                />
              }
              onClick={() => setShowEditModal(true)}
            >
              Add Team Member
            </Button>
          </SearchWrapper>
        )}

        {!initialLoading && (
          <>
            {type === "settings" ? (
              <OptedInLeads loading={loading} data={agency_details} />
            ) : type === "team_members" ? (
              <CommonTable
                data={teamMembers}
                columns={ManagerTableColumns}
                tableWrapperStyles="table-wrapper-styles"
                loading={loading}
                pagination={pagination}
                total_count={agency_details?.total_team_members_filtered}
                page={currentPage}
                limit={initialLimit}
                onPageChange={handlePageChange}
              />
            ) : (
              <CommonTable
                data={creators}
                columns={CreatorTableColumns}
                tableWrapperStyles="table-wrapper-styles"
                loading={loading}
                pagination={pagination}
                total_count={agency_details?.total_creators_filtered}
                page={currentPage}
                limit={initialLimit}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </CommonDashboardWrapper>

      <TalentAgencyManagerModal
        show={showEditModal}
        onHide={handleCloseModal}
        mode={modalMode}
        rowData={selectedRow}
      />

      <RemoveTeamMember
        show={removeModal.show}
        rowData={removeModal.row}
        step={removeModal.step}
        creators={removeModal.creators}
        loading={removeMember.loading}
        managersList={managers}
        onHide={() =>
          setRemoveModal({
            show: false,
            row: null,
            step: "confirm",
            creators: []
          })
        }
        onRemove={handleRemoveTeamMember}
        onReassignAndRemove={handleReassignAndRemove}
      />

      <RosterUpdateModal
        show={showRosterModal}
        onHide={() => setShowRosterModal(false)}
        onUpdate={(data) => {
          setShowRosterModal(false);
        }}
      />
    </>
  );
};

export default AgencyPage;
